import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import { Folder, Storage } from '@mui/icons-material';
import BaseTab from './BaseTab';

interface BackendStructureTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const BackendStructureTab: React.FC<BackendStructureTabProps> = (props) => {
  const getColor = (index: number) => {
    const colors = [props.goldColor, '#0F3B2E', '#1D5C4A', '#051814', props.goldColor, '#0F3B2E'];
    return colors[index % colors.length];
  };

  return (
    <BaseTab
      {...props}
      title="Backend Structure"
      emoji="📁"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Backend Project Structure
      </Typography>
      
      <Paper sx={{ bgcolor: alpha('#051814', 0.8), p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="body2" fontFamily="monospace" color={props.goldColor} sx={{ lineHeight: 1.8 }}>
          src/<br />
          ├── config/<br />
          │   └── passport.ts              # OAuth configuration<br />
          ├── controller/                  # Route handlers<br />
          │   ├── auth.controller.ts<br />
          │   ├── user.controller.ts<br />
          │   ├── community.controller.ts<br />
          │   ├── post.controller.ts<br />
          │   └── chat.controller.ts<br />
          ├── middleware/                  # Express middleware<br />
          │   ├── auth.middleware.ts<br />
          │   ├── upload.middleware.ts<br />
          │   └── admin.middleware.ts<br />
          ├── models/                      # MongoDB schemas<br />
          │   ├── User.ts<br />
          │   ├── Community.ts<br />
          │   ├── Post.ts<br />
          │   └── Chat.ts<br />
          ├── routes/                      # API route definitions<br />
          │   ├── auth.routes.ts<br />
          │   ├── user.routes.ts<br />
          │   ├── community.routes.ts<br />
          │   └── ...<br />
          ├── services/                    # External service integrations<br />
          │   ├── googleMaps.service.ts<br />
          └── index.ts                     # Application entry point
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha(props.goldColor, 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
                Core Directories
              </Typography>
              <List>
                {[
                  'config/ - Configuration files',
                  'controller/ - Route handlers',
                  'models/ - Database schemas',
                  'middleware/ - Express middleware',
                  'routes/ - API route definitions',
                  'services/ - External integrations',
                ].map((dir, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Folder sx={{ color: getColor(idx) }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={dir} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha('#0F3B2E', 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#0F3B2E' }}>
                Database Collections
              </Typography>
              <List>
                {[
                  'Users - User profiles & auth',
                  'Communities - Community data',
                  'Posts - User-generated content',
                  'Comments - Threaded comments',
                  'Chat - Real-time messaging',
                  'Reports - Content moderation',
                  'CafeVisits - Cafe tracking',
                ].map((collection, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Storage sx={{ color: getColor(idx) }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={collection} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </BaseTab>
  );
};

export default BackendStructureTab;